import type { AssessmentResult, WordScore } from '../types.ts';
import { normalize } from './text.ts';

export type WordTone = 'good' | 'ok' | 'bad' | 'none';

export function toneFor(accuracy: number): WordTone {
  if (accuracy >= 80) return 'good';
  if (accuracy >= 60) return 'ok';
  return 'bad';
}

/**
 * Kolor dla każdego wyświetlanego słowa zdania na podstawie wyniku oceny.
 */
export function wordTones(text: string, words: readonly WordScore[]): WordTone[] {
  return scoresForDisplayWords(text, words).map((w) => (w ? toneFor(w.accuracy) : 'none'));
}

/**
 * Ocena słowa dla każdego wyświetlanego słowa zdania (null, gdy nie da się sparować).
 * Słowa Insertion (nadmiarowe) są pomijane — nie ma ich w zdaniu wzorcowym.
 */
export function scoresForDisplayWords(text: string, words: readonly WordScore[]): (WordScore | null)[] {
  const display = text.trim().split(/\s+/).filter(Boolean);
  const scored = words.filter((w) => w.error !== 'Insertion');
  if (scored.length === display.length) return scored;
  // różna liczba (np. skrót "I'm" rozbity inaczej) — dopasuj po treści, kolejno
  let cursor = 0;
  return display.map((d) => {
    const n = normalize(d);
    for (let i = cursor; i < scored.length; i++) {
      const w = scored[i] as WordScore;
      if (normalize(w.word) === n) {
        cursor = i + 1;
        return w;
      }
    }
    return null;
  });
}

/** Transkrypt „leksykalny” (małe litery, bez interpunkcji) w czytelnej dla dziecka postaci: „i went food” → „I went food.” */
export function prettyHeard(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t === '') return '';
  const fixed = t.replace(/\bi\b(?=$|\s|')/g, 'I');
  const cap = fixed.charAt(0).toUpperCase() + fixed.slice(1);
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
}

/**
 * Jednoliniowy opis wyniku oceny dla rodzica / do strojenia progów:
 * co usłyszał silnik oraz ocena każdego słowa (i głosek, jeśli Azure je podał).
 */
/** Mowa zaczęta wcześniej niż tyle ms po otwarciu mikrofonu to prawie na pewno ucięty początek (mikrofon dopiero się „rozgrzewa”). */
export const ONSET_TOO_EARLY_MS = 150;

export function describeResult(r: AssessmentResult): string {
  const azureHeard = r.scriptedText && r.scriptedText !== r.recognizedText ? ` (Azure ze wzorcem: „${r.scriptedText}”)` : '';
  const heard = r.recognizedText ? `Usłyszałem: „${r.recognizedText}”${azureHeard}. ` : '';
  const words = r.words.map((w) => {
    const err = (w.error !== 'None' ? ` ${w.error}` : '') + (w.azureAccuracy !== undefined ? ` (Azure: ${Math.round(w.azureAccuracy)})` : '');
    const ph = w.phonemes?.length ? ` [${w.phonemes.map((p) => `${p.phoneme} ${Math.round(p.accuracy)}`).join(', ')}]` : '';
    return `${w.word} ${Math.round(w.accuracy)}${err}${ph}`;
  });
  const certainty =
    r.transcriptConfidence !== undefined
      ? ` Pewność rozpoznania ${r.transcriptConfidence.toFixed(2)}${r.transcriptTies !== undefined ? `, bliskich alternatyw ${r.transcriptTies}` : ''}.`
      : '';
  const exact = r.transcriptExact ? ' Transkrypt = zdanie.' : '';
  const onset =
    r.speechOnsetMs !== undefined
      ? ` Mowa zaczęła się ${r.speechOnsetMs} ms po otwarciu mikrofonu${r.speechOnsetMs < ONSET_TOO_EARLY_MS ? ' — od razu, więc pierwsze słowo mogło zostać ucięte' : ''}.`
      : '';
  const replayed = r.replays ? ` Połączenie z Azure zawiodło — rozpoznawanie powtórzono na nagraniu (${r.replays}×).` : '';
  return `${heard}Kompletność ${Math.round(r.completeness)}, dokładność ${Math.round(r.accuracy)}.${certainty}${exact}${onset}${replayed} Słowa: ${words.join(' · ') || '—'}`;
}
